import { Customer, ScanResult, Finding } from "../types/customer";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { formatDateTime, type TimezoneMode } from "../utils/dates";
import { apiUrl, fetchApiJson } from "../utils/api";
import type { Job } from "../types/job";
import { useEffect, useMemo, useRef, useState } from "react";
import type { AppSettings } from "../types/settings";

interface CustomerDetailModalProps {
  customer: Customer | null;
  open: boolean;
  onClose: () => void;
  onScan: (customer: Customer) => void;
  onDryRun: (customer: Customer) => void;
  onCancel: (customer: Customer) => void;
  onEdit: (customer: Customer) => void;
  pdfAvailable?: boolean;
  pdfGenerating?: boolean;
  onOpenPdf?: (customer: Customer) => void;
  onRemakePdf?: (customer: Customer) => void;
  onDelete: (customer: Customer) => void;
  onClearResults: (customer: Customer) => void;
  scanResults: ScanResult[];
  timezone: TimezoneMode;
  runningJob: Job | null;
}

function SeverityPill({ severity }: { severity: Finding["severity"] }) {
  const variant =
    severity === "critical" || severity === "high"
      ? "danger"
      : severity === "medium"
        ? "warning"
        : severity === "low"
          ? "info"
          : "neutral";

  return <Badge variant={variant}>{severity.toUpperCase()}</Badge>;
}

function formatScannerLabel(scanner: ScanResult["scanner"]) {
  if (scanner === "sitecheck") return "Site Checks";
  if (scanner === "httpx") return "httpx";
  if (scanner === "wpscan") return "WPScan";
  return "Nuclei";
}

function rankSeverity(severity: Finding["severity"]) {
  if (severity === "critical") return 4;
  if (severity === "high") return 3;
  if (severity === "medium") return 2;
  if (severity === "low") return 1;
  return 0;
}

function getLatestTimestamp(results: ScanResult[]) {
  if (!Array.isArray(results) || results.length === 0) return null;
  let best = results[0]?.timestamp || null;
  for (const r of results) {
    if (!r?.timestamp) continue;
    if (!best) {
      best = r.timestamp;
      continue;
    }
    if (String(r.timestamp) > String(best)) best = r.timestamp;
  }
  return best;
}

function formatDurationMs(ms: number) {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  if (m > 0) return `${m}m ${String(sec).padStart(2, "0")}s`;
  return `${sec}s`;
}

function computeEffectiveWpscanFlags({
  mode,
  profile,
  customerConfig,
}: {
  mode: "stealth" | "custom";
  profile: "stealth" | "thorough";
  customerConfig: Customer["config"];
}): { flags: string[]; note?: string } {
  if (mode === "stealth") {
    const stealthEnum = profile === "thorough" ? "u,ap,at,tt,cb,dbe,m" : "u,p,t";
    return {
      flags: ["--enumerate", stealthEnum, "--random-user-agent", "--disable-tls-checks", "--force"],
      note:
        profile === "thorough"
          ? "Stealth/Thorough runs a fixed profile."
          : "Stealth runs a fixed profile.",
    };
  }

  const flags: string[] = [];
  const enumParts: string[] = [];
  if (customerConfig.enumerateUsers) enumParts.push("u");
  if (customerConfig.enumerateVulnerablePlugins) enumParts.push("vp");
  if (customerConfig.enumerateAllPlugins) enumParts.push("ap");
  if (customerConfig.enumerateVulnerableThemes) enumParts.push("vt");
  if (customerConfig.enumerateAllThemes) enumParts.push("at");
  if (enumParts.length > 0) flags.push("--enumerate", enumParts.join(","));
  if (customerConfig.randomUserAgent) flags.push("--random-user-agent");
  if (customerConfig.disableTls) flags.push("--disable-tls-checks");
  if (customerConfig.forceScan) flags.push("--force");
  if (customerConfig.proxyUrl) flags.push("--proxy", customerConfig.proxyUrl);
  if (customerConfig.passwordList) flags.push("--passwords", customerConfig.passwordList);

  return { flags, note: "Custom mode uses the per-customer options shown here." };
}

export function CustomerDetailModal({
  customer,
  open,
  onClose,
  onScan,
  onDryRun,
  onCancel,
  onEdit,
  pdfAvailable = false,
  pdfGenerating = false,
  onOpenPdf,
  onRemakePdf,
  onDelete,
  onClearResults,
  scanResults,
  timezone,
  runningJob,
}: CustomerDetailModalProps) {
  const [liveLog, setLiveLog] = useState("");
  const liveLogRef = useRef<HTMLPreElement | null>(null);
  const runningJobId = runningJob?.id ?? null;
  const runningJobStartedAt = runningJob?.startedAt ?? null;
  const [globalSettings, setGlobalSettings] = useState<Partial<AppSettings> | null>(null);
  const [jobHistory, setJobHistory] = useState<Job[]>([]);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (!open || !runningJobId) return;

    setLiveLog("");
    const es = new EventSource(apiUrl(`/api/jobs/${runningJobId}/events`));

    const onInit = (event: MessageEvent) => {
      try {
        const payload = JSON.parse(event.data);
        const chunks = Array.isArray(payload?.chunks) ? payload.chunks : [];
        setLiveLog(chunks.join(""));
      } catch {
        // ignore
      }
    };

    const onLog = (event: MessageEvent) => {
      try {
        const payload = JSON.parse(event.data);
        const chunk = typeof payload?.chunk === "string" ? payload.chunk : "";
        if (!chunk) return;
        setLiveLog((prev) => `${prev}${chunk}`);
      } catch {
        // ignore
      }
    };

    const onDone = () => {
      es.close();
    };

    es.addEventListener("init", onInit as unknown as EventListener);
    es.addEventListener("log", onLog as unknown as EventListener);
    es.addEventListener("done", onDone as unknown as EventListener);

    return () => {
      es.close();
    };
  }, [open, runningJobId, runningJobStartedAt]);

  useEffect(() => {
    const el = liveLogRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [liveLog]);

  useEffect(() => {
    if (!open) return;
    let active = true;
    (async () => {
      try {
        const json = await fetchApiJson<{ jobs?: Job[] }>("/api/state");
        if (!active) return;
        setJobHistory(Array.isArray(json.jobs) ? json.jobs : []);
      } catch {
        // ignore
      }
    })();
    return () => {
      active = false;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !runningJobId) return;
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [open, runningJobId]);

  useEffect(() => {
    if (!open) return;
    let active = true;
    (async () => {
      try {
        const json = await fetchApiJson<{ settings?: Partial<AppSettings> }>("/api/settings");
        if (!active) return;
        setGlobalSettings(json.settings || {});
      } catch {
        // ignore
      }
    })();
    return () => {
      active = false;
    };
  }, [open]);

  const showLive = Boolean(open && runningJobId);
  const customerResults = useMemo(() => {
    if (!customer) return [];
    return scanResults.filter((r) => r.customerId === customer.id);
  }, [customer, scanResults]);
  const logsByScan = useMemo(() => {
    const groups = new Map<string, { scanSeq?: number; timestamp?: string; mode?: ScanResult["mode"]; results: ScanResult[] }>();
    for (const r of customerResults) {
      const key =
        typeof r.scanSeq === "number" ? `seq:${r.scanSeq}` : r.jobId ? `job:${r.jobId}` : r.timestamp ? `ts:${r.timestamp}` : `id:${r.id}`;
      const existing = groups.get(key) || { scanSeq: r.scanSeq, timestamp: r.timestamp, mode: r.mode, results: [] };
      existing.scanSeq = typeof existing.scanSeq === "number" ? existing.scanSeq : r.scanSeq;
      existing.timestamp = existing.timestamp || r.timestamp;
      existing.mode = existing.mode || r.mode;
      existing.results.push(r);
      groups.set(key, existing);
    }
    const list = Array.from(groups.values());
    list.sort((a, b) => {
      const as = typeof a.scanSeq === "number" ? a.scanSeq : -1;
      const bs = typeof b.scanSeq === "number" ? b.scanSeq : -1;
      if (as !== bs) return bs - as;
      return String(b.timestamp || "").localeCompare(String(a.timestamp || ""));
    });
    return list;
  }, [customerResults]);

  const avgScanDurationMs = useMemo(() => {
    if (!customer) return null;
    const durations: number[] = [];
    for (const j of jobHistory) {
      if (j.customerId !== customer.id) continue;
      if (!j.startedAt || !j.finishedAt) continue;
      const start = Date.parse(j.startedAt);
      const end = Date.parse(j.finishedAt);
      if (!Number.isFinite(start) || !Number.isFinite(end)) continue;
      const d = end - start;
      if (d > 5_000 && d < 12 * 60 * 60 * 1000) durations.push(d);
    }
    if (durations.length === 0) return null;
    durations.sort((a, b) => a - b);
    const recent = durations.slice(-8);
    const sum = recent.reduce((acc, v) => acc + v, 0);
    return Math.round(sum / recent.length);
  }, [customer, jobHistory]);

  const liveStartMs = useMemo(() => {
    const ms = runningJobStartedAt ? Date.parse(runningJobStartedAt) : NaN;
    return Number.isFinite(ms) ? ms : null;
  }, [runningJobStartedAt]);

  const liveElapsedMs = useMemo(() => {
    if (!showLive || !liveStartMs) return null;
    return Math.max(0, nowMs - liveStartMs);
  }, [showLive, liveStartMs, nowMs]);

  const liveEtaMs = useMemo(() => {
    if (!showLive || !liveStartMs || !avgScanDurationMs) return null;
    return liveStartMs + avgScanDurationMs;
  }, [showLive, liveStartMs, avgScanDurationMs]);

  const latestRunTimestamp = useMemo(() => customer?.lastScan || getLatestTimestamp(customerResults), [customer, customerResults]);
  const latestRunResults = useMemo(() => {
    if (!latestRunTimestamp) return [];
    return customerResults.filter((r) => r.timestamp === latestRunTimestamp);
  }, [customerResults, latestRunTimestamp]);
  const latestRunFindings = useMemo(() => latestRunResults.flatMap((r) => r.findings || []), [latestRunResults]);

  const latestRunSeverityCounts = useMemo(() => {
    const counts = { critical: 0, high: 0, medium: 0, low: 0, info: 0, total: 0 };
    for (const f of latestRunFindings) {
      const s = f?.severity || "info";
      if (s === "critical") counts.critical += 1;
      else if (s === "high") counts.high += 1;
      else if (s === "medium") counts.medium += 1;
      else if (s === "low") counts.low += 1;
      else counts.info += 1;
      counts.total += 1;
    }
    return counts;
  }, [latestRunFindings]);

  const latestRunScannerStatus = useMemo(() => {
    const byScanner = new Map<string, ScanResult["status"]>();
    for (const r of latestRunResults) byScanner.set(r.scanner, r.status);
    const order: ScanResult["scanner"][] = ["sitecheck", "httpx", "wpscan", "nuclei"];
    return order
      .filter((s) => byScanner.has(s))
      .map((s) => ({ scanner: s, status: byScanner.get(s)! }));
  }, [latestRunResults]);

  const latestRunScanSeq = useMemo(() => {
    const first = latestRunResults[0];
    return typeof first?.scanSeq === "number" ? first.scanSeq : null;
  }, [latestRunResults]);

  const keyFindingsPreview = useMemo(() => {
    const sorted = latestRunFindings
      .slice()
      .sort((a, b) => rankSeverity(b.severity) - rankSeverity(a.severity) || String(a.title).localeCompare(String(b.title)));
    return sorted.slice(0, 6);
  }, [latestRunFindings]);

  const latestWpScan = useMemo(() => {
    for (let i = customerResults.length - 1; i >= 0; i -= 1) {
      if (customerResults[i].scanner === "wpscan") return customerResults[i];
    }
    return undefined;
  }, [customerResults]);

  const latestNuclei = useMemo(() => {
    for (let i = customerResults.length - 1; i >= 0; i -= 1) {
      if (customerResults[i].scanner === "nuclei") return customerResults[i];
    }
    return undefined;
  }, [customerResults]);

  if (!customer) return null;

  const globalScannerSettings = globalSettings?.scanners && typeof globalSettings.scanners === "object" ? globalSettings.scanners : null;
  const effectiveWpscanTokenSource = customer.config.wpscanApiToken ? "custom" : globalScannerSettings?.wpscanApiToken ? "global" : "env";
  const effectiveVulnDbEnrich =
    typeof customer.config.wpscanVulnDbEnrich === "boolean"
      ? customer.config.wpscanVulnDbEnrich
      : globalScannerSettings?.wpscanVulnDbEnrich !== false;
  const effectiveWpscanProfile =
    globalScannerSettings?.wpscanProfile === "thorough" || globalScannerSettings?.wpscanProfile === "stealth"
      ? globalScannerSettings.wpscanProfile
      : "stealth";
  const effectiveWpscanMode = (customer.config.wpscanMode ?? "stealth") === "custom" ? "custom" : "stealth";
  const effectiveWpscan = computeEffectiveWpscanFlags({
    mode: effectiveWpscanMode,
    profile: effectiveWpscanProfile,
    customerConfig: customer.config,
  });
  const isLead = customer.source === "free_scan" || customer.lead === true;
  const leadTags = Array.isArray(customer.tags) ? customer.tags.filter((tag) => String(tag || "").trim()) : [];
  const leadEmail =
    customer.leadEmail ||
    (typeof customer.contact === "string" && customer.contact.includes("@") ? customer.contact : undefined);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-6xl bg-card border-border max-h-[90vh] overflow-hidden flex flex-col min-h-0">
        <DialogHeader className="flex flex-row items-start justify-between">
          <div>
            <DialogTitle className="text-xl font-semibold text-foreground">
              {customer.name}
            </DialogTitle>
            <p className="text-sm text-muted-foreground">{customer.url}</p>
          </div>
          {/* DialogContent already provides a top-right close button */}
        </DialogHeader>

	        <div className="mt-4 flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden pb-10 pr-1">
	        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
            onClick={() => onScan(customer)}
          >
            Scan
          </Button>
          <Button size="sm" variant="secondary" onClick={() => onDryRun(customer)}>
            Dry run
          </Button>
          <Button size="sm" variant="destructive" onClick={() => onCancel(customer)}>
            Cancel
          </Button>
          {isLead && (
            <Button size="sm" variant="secondary" onClick={() => onEdit(customer)}>
              Convert to Customer
            </Button>
          )}
	          <Button size="sm" variant="secondary" onClick={() => onEdit(customer)}>
	            Edit
	          </Button>
	          <Button
	            size="sm"
	            variant="secondary"
	            disabled={!pdfAvailable}
	            onClick={() => onOpenPdf?.(customer)}
	          >
	            Open PDF
	          </Button>
	          <Button
	            size="sm"
	            variant="secondary"
	            disabled={pdfGenerating || !customer.lastScan}
	            onClick={() => onRemakePdf?.(customer)}
	          >
	            {pdfGenerating ? "Remaking..." : "Remake PDF"}
	          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" variant="destructive">
                Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete customer?</AlertDialogTitle>
                <AlertDialogDescription>
                  This removes the customer and their saved scan results from the portal.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => onDelete(customer)}>
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" variant="secondary">
                Clear results
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Clear results and logs?</AlertDialogTitle>
                <AlertDialogDescription>
                  This deletes saved scan results and logs for this customer.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => onClearResults(customer)}>
                  Clear
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>

        <div className="mt-6 rounded-lg border border-border bg-muted/20 p-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">URL</p>
              <p className="text-sm font-medium text-primary">{customer.url}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Last scan</p>
              <p className="text-sm font-medium text-foreground">
                {formatDateTime(customer.lastScan, timezone)}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Contact</p>
              <p className="text-sm font-medium text-foreground">{customer.contact}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Status</p>
              <Badge
                variant={
                  customer.status === "error"
                    ? "danger"
                    : customer.status === "scanning"
                      ? "info"
                      : customer.status === "completed"
                        ? "success"
                        : "neutral"
                }
              >
                {customer.status.replace(/_/g, " ")}
              </Badge>
            </div>
          </div>

          {customer.lastError && (
            <div className="mt-4 rounded border border-[hsl(var(--ss-danger)/0.3)] bg-[hsl(var(--ss-danger)/0.08)] p-3">
              <p className="text-sm text-[hsl(var(--ss-danger))]">Last error</p>
              <pre className="mt-2 max-h-28 overflow-y-auto whitespace-pre-wrap break-words font-mono text-xs text-[hsl(var(--ss-danger))] pb-2">
                {customer.lastError}
              </pre>
            </div>
          )}
        </div>

        <Tabs defaultValue="overview" className="mt-4">
          <TabsList className="bg-muted">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="results">Results</TabsTrigger>
            <TabsTrigger value="logs">Scan Logs</TabsTrigger>
          </TabsList>
        <TabsContent value="overview" className="mt-4 min-h-0">
          <div className="rounded-lg border border-border bg-muted/10 p-4 min-h-[200px]">
            {isLead && (
              <div className="mb-4 rounded border border-[hsl(var(--ss-warning)/0.3)] bg-[hsl(var(--ss-warning)/0.08)] p-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-foreground">Lead</p>
                    <p className="text-xs text-muted-foreground">Live Scan lead details</p>
                  </div>
                  <Badge variant="warning">
                    {customer.leadType || "live_scan"}
                  </Badge>
                </div>
                <div className="mt-3 grid gap-2 text-sm md:grid-cols-2">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-muted-foreground">Lead email</span>
                    <span className="font-medium text-foreground">{leadEmail || "-"}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-muted-foreground">Created</span>
                    <span className="font-medium text-foreground">
                      {formatDateTime(customer.leadCreatedAt || customer.createdAt, timezone)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-muted-foreground">Last seen</span>
                    <span className="font-medium text-foreground">
                      {formatDateTime(customer.leadLastSeenAt || customer.updatedAt, timezone)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-muted-foreground">Tags</span>
                    <span className="font-medium text-foreground">
                      {leadTags.length > 0 ? leadTags.join(", ") : "-"}
                    </span>
                  </div>
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="rounded border border-border bg-card p-3">
                <p className="text-xs text-muted-foreground">Latest scan</p>
                {typeof latestRunScanSeq === "number" && (
                  <p className="mt-1 text-sm font-medium text-foreground">
                    Scan #{String(latestRunScanSeq).padStart(5, "0")}
                  </p>
                )}
                <p className="mt-1 text-sm font-medium text-foreground">{formatDateTime(latestRunTimestamp, timezone)}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {latestRunScannerStatus.length > 0 ? (
                    latestRunScannerStatus.map((s) => (
                      <Badge
                        key={s.scanner}
                        variant={
                          s.status === "success"
                            ? "success"
                            : s.status === "warning"
                              ? "warning"
                              : "danger"
                        }
                      >
                        {formatScannerLabel(s.scanner)}: {s.status}
                      </Badge>
                    ))
                  ) : (
                    <Badge variant="neutral">No results yet</Badge>
                  )}
                </div>
              </div>

              <div className="rounded border border-border bg-card p-3">
                <p className="text-xs text-muted-foreground">Findings (latest scan)</p>
                <div className="mt-2 flex flex-wrap gap-2 text-sm">
                  <Badge variant="neutral">Total: {latestRunSeverityCounts.total}</Badge>
                  <Badge variant="danger">Critical: {latestRunSeverityCounts.critical}</Badge>
                  <Badge variant="danger">High: {latestRunSeverityCounts.high}</Badge>
                  <Badge variant="warning">Medium: {latestRunSeverityCounts.medium}</Badge>
                  <Badge variant="info">Low: {latestRunSeverityCounts.low}</Badge>
                  <Badge variant="neutral">Info: {latestRunSeverityCounts.info}</Badge>
                </div>
              </div>

              <div className="rounded border border-border bg-card p-3">
                <p className="text-xs text-muted-foreground">Effective scan config</p>
                <div className="mt-2 space-y-1 text-sm text-foreground">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-muted-foreground">WPScan mode</span>
                    <span className="font-medium">
                      {effectiveWpscanMode}
                      {effectiveWpscanMode === "stealth" ? ` (${effectiveWpscanProfile})` : ""}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-muted-foreground">VulnDB enrichment</span>
                    <span className="font-medium">{effectiveVulnDbEnrich ? "enabled" : "disabled"}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-muted-foreground">WPScan token source</span>
                    <span className="font-medium">{effectiveWpscanTokenSource}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-muted-foreground">Nuclei</span>
                    <span className="font-medium">
                      {customer.config.runNuclei
                        ? `enabled${customer.config.nucleiTemplatesPath ? ` (${customer.config.nucleiTemplatesPath})` : ""}`
                        : "disabled"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="rounded border border-border bg-card p-3">
                <p className="text-sm font-medium text-foreground">WPScan effective flags</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {effectiveWpscan.flags.length > 0 ? effectiveWpscan.flags.join(" ") : "-"}
                </p>
                {effectiveWpscan.note && (
                  <p className="mt-2 text-xs text-muted-foreground">{effectiveWpscan.note}</p>
                )}
              </div>

              <div className="rounded border border-border bg-card p-3">
                <p className="text-sm font-medium text-foreground">Key findings (latest scan)</p>
                {keyFindingsPreview.length > 0 ? (
                  <div className="mt-2 space-y-2">
                    {keyFindingsPreview.map((f) => (
                      <div key={f.id} className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm text-foreground">{f.title}</p>
                          <p className="truncate text-xs text-muted-foreground">{f.scanner}</p>
                        </div>
                        <SeverityPill severity={f.severity} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mt-2 text-sm text-muted-foreground">No findings yet. Run a scan to populate results.</p>
                )}
              </div>
            </div>
          </div>
        </TabsContent>
        <TabsContent value="results" className="mt-4 min-h-0">
          <Tabs defaultValue="sitecheck" className="flex min-h-0 flex-1 flex-col">
            <TabsList className="bg-muted">
              <TabsTrigger value="sitecheck">Site Checks</TabsTrigger>
              <TabsTrigger value="httpx">httpx</TabsTrigger>
              <TabsTrigger value="wpscan">WPScan Results</TabsTrigger>
              <TabsTrigger value="nuclei">Nuclei Results</TabsTrigger>
            </TabsList>
            <TabsContent value="sitecheck" className="mt-4 min-h-0">
            {(() => {
              const latestSitecheck = (() => {
                for (let i = customerResults.length - 1; i >= 0; i -= 1) {
                  if (customerResults[i].scanner === "sitecheck") return customerResults[i];
                }
                return undefined;
              })();

		              return latestSitecheck ? (
		                <div className="rounded-lg border border-border bg-muted/10 p-4 min-h-[200px]">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">
                      {formatDateTime(latestSitecheck.timestamp, timezone)}
                    </div>
                    <Badge
                      variant={
                        latestSitecheck.status === "success"
                          ? "success"
                          : latestSitecheck.status === "warning"
                            ? "warning"
                            : "danger"
                      }
                    >
                      {latestSitecheck.status}
                    </Badge>
                  </div>

                  {latestSitecheck.findings.length > 0 ? (
                    <div className="mt-4 space-y-2">
                      {latestSitecheck.findings.map((f) => (
                        <div key={f.id} className="rounded border border-border bg-card p-3">
                          <div className="flex items-center justify-between gap-3">
                            <div className="font-medium text-foreground">{f.title}</div>
                            <SeverityPill severity={f.severity} />
                          </div>
                          <p className="mt-1 text-sm text-muted-foreground">{f.description}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-4 text-muted-foreground text-sm">No site issues detected.</p>
                  )}

                  <div className="mt-4 max-w-full overflow-x-hidden rounded border border-border bg-muted/10 p-3 font-mono text-xs">
                    <pre className="max-w-full whitespace-pre-wrap break-all text-foreground">{latestSitecheck.rawOutput || "-"}</pre>
                  </div>
                </div>
	              ) : (
	                <div className="rounded-lg border border-border bg-muted/10 p-4 min-h-[200px]">
	                  <p className="text-muted-foreground">No site checks available.</p>
	                </div>
	              );
            })()}
          </TabsContent>
          <TabsContent value="httpx" className="mt-4 min-h-0">
            {(() => {
              const latestHttpx = (() => {
                for (let i = customerResults.length - 1; i >= 0; i -= 1) {
                  if (customerResults[i].scanner === "httpx") return customerResults[i];
                }
                return undefined;
              })();

	              return latestHttpx ? (
	                <div className="rounded-lg border border-border bg-muted/10 p-4 min-h-[200px]">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">
                      {formatDateTime(latestHttpx.timestamp, timezone)}
                    </div>
                    <Badge
                      variant={
                        latestHttpx.status === "success"
                          ? "success"
                          : latestHttpx.status === "warning"
                            ? "warning"
                            : "danger"
                      }
                    >
                      {latestHttpx.status}
                    </Badge>
                  </div>

                  {latestHttpx.findings.length > 0 ? (
                    <div className="mt-4 space-y-2">
                      {latestHttpx.findings.map((f) => (
                        <div key={f.id} className="rounded border border-border bg-card p-3">
                          <div className="flex items-center justify-between gap-3">
                            <div className="font-medium text-foreground">{f.title}</div>
                            <SeverityPill severity={f.severity} />
                          </div>
                          <p className="mt-1 text-sm text-muted-foreground">{f.description}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-4 text-muted-foreground text-sm">No httpx output was parsed.</p>
                  )}

                  <div className="mt-4 max-w-full overflow-x-hidden rounded border border-border bg-muted/10 p-3 font-mono text-xs">
                    <pre className="max-w-full whitespace-pre-wrap break-all text-foreground">{latestHttpx.rawOutput || "-"}</pre>
                  </div>
                </div>
	              ) : (
	                <div className="rounded-lg border border-border bg-muted/10 p-4 min-h-[200px]">
	                  <p className="text-muted-foreground">No httpx results available.</p>
	                </div>
	              );
            })()}
          </TabsContent>
          <TabsContent value="wpscan" className="mt-4 min-h-0">
            {latestWpScan ? (
              <div className="rounded-lg border border-border bg-muted/10 p-4 min-h-[200px]">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    {formatDateTime(latestWpScan.timestamp, timezone)}
                  </div>
                  <Badge
                    variant={
                      latestWpScan.status === "success"
                        ? "success"
                        : latestWpScan.status === "warning"
                          ? "warning"
                          : "danger"
                    }
                  >
                    {latestWpScan.status}
                  </Badge>
                </div>

                {latestWpScan.findings.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {latestWpScan.findings.map((f) => (
                      <div key={f.id} className="rounded border border-border bg-card p-3">
                        <div className="flex items-center justify-between gap-3">
                          <div className="font-medium text-foreground">{f.title}</div>
                          <SeverityPill severity={f.severity} />
                        </div>
                        <p className="mt-1 text-sm text-muted-foreground">{f.description}</p>
                        {f.reference && (
                          <p className="mt-1 text-xs text-primary">Reference: {f.reference}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-4 max-w-full overflow-x-hidden rounded border border-border bg-muted/10 p-3 font-mono text-xs">
                  <pre className="max-w-full whitespace-pre-wrap break-all text-foreground">{latestWpScan.rawOutput || "-"}</pre>
                </div>
              </div>
            ) : (
              <div className="rounded-lg border border-border bg-muted/10 p-4 min-h-[200px]">
                <p className="text-muted-foreground">No WPScan results available.</p>
              </div>
            )}
          </TabsContent>
          <TabsContent value="nuclei" className="mt-4 min-h-0">
            {!customer.config.runNuclei ? (
              <div className="rounded-lg border border-border bg-muted/10 p-4 min-h-[200px]">
                <p className="text-muted-foreground text-sm">Nuclei is disabled for this customer.</p>
              </div>
            ) : latestNuclei ? (
              <div className="rounded-lg border border-border bg-muted/10 p-4 min-h-[200px]">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    {formatDateTime(latestNuclei.timestamp, timezone)}
                  </div>
                  <Badge
                    variant={
                      latestNuclei.status === "success"
                        ? "success"
                        : latestNuclei.status === "warning"
                          ? "warning"
                          : "danger"
                    }
                  >
                    {latestNuclei.status}
                  </Badge>
                </div>

                {latestNuclei.findings.length > 0 ? (
                  <div className="mt-4 space-y-2">
                    {latestNuclei.findings.map((f) => (
                      <div key={f.id} className="rounded border border-border bg-card p-3">
                        <div className="flex items-center justify-between gap-3">
                          <div className="font-medium text-foreground">{f.title}</div>
                          <SeverityPill severity={f.severity} />
                        </div>
                        <p className="mt-1 text-sm text-muted-foreground">{f.description}</p>
                        {f.reference && (
                          <p className="mt-1 text-xs text-primary">Reference: {f.reference}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mt-4 text-muted-foreground text-sm">No findings reported.</p>
                )}

                <div className="mt-4 max-w-full overflow-x-hidden rounded border border-border bg-muted/10 p-3 font-mono text-xs">
                  <pre className="max-w-full whitespace-pre-wrap break-all text-foreground">{latestNuclei.rawOutput || "-"}</pre>
                </div>
              </div>
            ) : (
              <div className="rounded-lg border border-border bg-muted/10 p-4 min-h-[200px]">
                <p className="text-muted-foreground text-sm">No Nuclei results available.</p>
              </div>
            )}
          </TabsContent>
          </Tabs>
        </TabsContent>
        <TabsContent value="logs" className="mt-4 min-h-0">
            <div className="rounded-lg border border-border bg-muted/10 p-4 min-h-[200px] font-mono text-xs">
              {showLive && (
                <div className="mb-3 rounded border border-border bg-card p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-muted-foreground">
                    <span>Live · job {runningJobId}</span>
                    <span>running</span>
                  </div>
                  <div className="mt-2 grid grid-cols-1 gap-2 text-[11px] text-muted-foreground sm:grid-cols-3">
                    <div>
                      <span className="text-muted-foreground">Start:</span>{" "}
                      <span className="text-foreground">{formatDateTime(runningJobStartedAt, timezone)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Elapsed:</span>{" "}
                      <span className="text-foreground">{liveElapsedMs !== null ? formatDurationMs(liveElapsedMs) : "-"}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Est. finish:</span>{" "}
                      <span className="text-foreground">
                        {liveEtaMs !== null ? formatDateTime(new Date(liveEtaMs).toISOString(), timezone) : "-"}
                      </span>
                    </div>
                  </div>
                  <pre
                    ref={liveLogRef}
                    className="mt-2 max-h-56 max-w-full overflow-x-hidden overflow-y-auto whitespace-pre-wrap break-all text-foreground pb-3"
                  >
                    {liveLog || "Waiting for output..."}
                  </pre>
                </div>
              )}

              {logsByScan.length === 0 ? (
                <p className="text-muted-foreground">No saved logs available.</p>
              ) : (
                <Accordion type="multiple" className="space-y-2">
                  {logsByScan.slice(0, 12).map((group) => {
                    const seq =
                      typeof group.scanSeq === "number" ? String(group.scanSeq).padStart(5, "0") : null;
                    const label = seq ? `Scan #${seq}` : "Scan";
                    const ts = group.timestamp ? formatDateTime(group.timestamp, timezone) : "-";
                    const worst = group.results.reduce<ScanResult["status"]>((acc, r) => {
                      if (acc === "error" || r.status === "error") return "error";
                      if (acc === "warning" || r.status === "warning") return "warning";
                      return "success";
                    }, "success");
                    const scanners = group.results
                      .slice()
                      .sort((a, b) => String(a.scanner).localeCompare(String(b.scanner)))
                      .map((r) => `${formatScannerLabel(r.scanner)}:${r.status}`)
                      .join(" · ");
                    const value = seq ? `scan-${seq}` : `scan-${ts}`;
                    return (
                      <AccordionItem key={value} value={value} className="rounded border border-border bg-card px-3">
                        <AccordionTrigger className="py-2 text-xs text-muted-foreground hover:no-underline">
                          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                            <span className="text-foreground">{label}</span>
                            <span>·</span>
                            <span>{ts}</span>
                            {group.mode && (
                              <>
                                <span>·</span>
                                <span>{group.mode}</span>
                              </>
                            )}
                            <span>·</span>
                            <span>{worst}</span>
                            {scanners ? (
                              <>
                                <span className="hidden sm:inline">·</span>
                                <span className="hidden sm:inline">{scanners}</span>
                              </>
                            ) : null}
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="pt-2">
                          <div className="space-y-3 pb-3">
                            {group.results
                              .slice()
                              .sort((a, b) => String(a.scanner).localeCompare(String(b.scanner)))
                              .map((r) => (
                                <div key={r.id} className="rounded border border-border bg-muted/10 p-3">
                                  <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                                    <span>{formatScannerLabel(r.scanner)}</span>
                                    <span>{r.status}</span>
                                  </div>
                                  <pre className="mt-2 max-w-full whitespace-pre-wrap break-all text-foreground">{r.rawOutput || "-"}</pre>
                                </div>
                              ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              )}
	            </div>
	          </TabsContent>
	        </Tabs>
          </div>
      </DialogContent>
    </Dialog>
  );
}
